import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Image } from "@/components/ui/image";
import CategoryGlyph from "@/components/yts/CategoryGlyph";

/**
 * Shared photo gallery used by local listings and partner experiences.
 * Auto-advances only while visible, with manual controls and reduced-motion support.
 */
export default function ListingGallery({ images = [], alt = "", onOpen }) {
  const [active, setActive] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const galleryRef = useRef(null);
  const count = images.length;
  const current = Math.min(active, Math.max(count - 1, 0));

  useEffect(() => setActive(0), [images[0]]);

  // Only run the slideshow while the gallery is on screen and the tab is active.
  useEffect(() => {
    const node = galleryRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15)),
      { threshold: [0, 0.15, 0.5] }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (count < 2 || !isVisible || !isTabVisible || isPaused || reducedMotion) return undefined;
    const timer = window.setInterval(() => {
      setActive((previous) => (previous + 1) % count);
    }, 4200);
    return () => window.clearInterval(timer);
  }, [count, isVisible, isTabVisible, isPaused, reducedMotion]);

  useEffect(() => {
    const updateVisibility = () => setIsTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", updateVisibility);
    updateVisibility();
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  const imageFrame = "relative overflow-hidden rounded-[14px] sm:rounded-[18px] bg-[#e9e9e3] dark:bg-[#242520]";
  const imageClass = "pointer-events-none block h-full w-full object-cover select-none";
  const buttonBase = "relative block min-w-0 overflow-hidden rounded-[8px] sm:rounded-[10px] bg-[#e9e9e3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#171717] focus-visible:ring-offset-2";

  if (count === 0) {
    return (
      <div className={imageFrame + " grid h-[260px] place-items-center sm:h-[420px]"}>
        <CategoryGlyph item={{ title: alt }} className="h-10 w-10 text-[#9CA3AF]" />
      </div>
    );
  }

  return (
    <section
      ref={galleryRef}
      aria-label={`${alt} photo gallery`}
      className="w-full"
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsPaused(false);
      }}
    >
      <div className={imageFrame + " group"}>
        <button
          type="button"
          onClick={() => onOpen?.(current)}
          aria-label={`View photo ${current + 1} of ${count} for ${alt}`}
          className="block w-full aspect-[4/3] sm:aspect-[16/9] sm:max-h-[500px] [touch-action:pan-y] cursor-zoom-in"
        >
          <Image
            src={images[current]}
            alt={alt}
            fittingType="fill"
            className={imageClass}
          />
        </button>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/35 to-transparent" />
        <div className="absolute bottom-3 left-3 rounded-full border border-white/25 bg-black/45 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white backdrop-blur-md">
          {count > 1 ? `Photo ${current + 1} of ${count}` : "Gallery"}
        </div>
        {count > 1 && (
          <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-between px-3 sm:px-4">
            <button type="button" onClick={() => setActive((current + count - 1) % count)} aria-label="Previous photo" className="grid h-10 w-10 sm:h-11 sm:w-11 place-items-center rounded-full border border-white/30 bg-black/45 text-white shadow-sm backdrop-blur-md transition hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button type="button" onClick={() => setActive((current + 1) % count)} aria-label="Next photo" className="grid h-10 w-10 sm:h-11 sm:w-11 place-items-center rounded-full border border-white/30 bg-black/45 text-white shadow-sm backdrop-blur-md transition hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          {count > 1 && (
            <button type="button" onClick={() => setIsPaused((paused) => !paused)} aria-label={isPaused ? "Resume slideshow" : "Pause slideshow"} aria-pressed={isPaused} className="grid h-9 w-9 place-items-center rounded-full border border-white/25 bg-black/45 text-white backdrop-blur-md transition hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
              {isPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            </button>
          )}
          <button type="button" onClick={() => onOpen?.(current)} className="rounded-full border border-white/25 bg-black/45 px-3 py-1.5 text-[10px] font-bold text-white backdrop-blur-md transition-colors hover:bg-black/70" aria-label="Expand photo">
            View photo ↗
          </button>
        </div>
      </div>

      {count > 1 && (
        <div className="mt-2 flex gap-2 overflow-x-auto overscroll-x-contain pb-1 [touch-action:pan-x] [scrollbar-width:thin]" aria-label="Choose a photo">
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Show photo ${i + 1}`}
              aria-pressed={i === current}
              className={`${buttonBase} h-[58px] w-[68px] shrink-0 border-2 sm:h-[70px] sm:w-[88px] ${i === current ? "border-[#171717] ring-1 ring-[#171717]" : "border-transparent opacity-70 hover:opacity-100"}`}
            >
              <Image src={src} alt="" fittingType="fill" className={imageClass} />
            </button>
          ))}
          <div className="flex shrink-0 items-center pl-1 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#777773]">
            {count} photos
          </div>
        </div>
      )}
    </section>
  );
}
